import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffectivePermissions, scopeSetFromPermissions } from "../../hooks/useEffectivePermissions";
import { useAuthContext } from "../../context/AuthContext";
import { useUsers } from "../../hooks/useUsers";
import {
  useTeamBoard,
  useTeamBoardForSpace,
  useTeamBoardForLead,
  useTeamBoardMutations,
} from "../../hooks/useTeamBoards";
import BoardColumn from "./components/BoardColumn";
import TaskCardModalContainer from "./components/TaskCardModalContainer";
import BoardHeader from "./components/BoardHeader";
import BoardFiltersBar from "./components/BoardFiltersBar";
import ChannelBoardToolbar from "./components/ChannelBoardToolbar";
import BoardMembersDialog from "./components/BoardMembersDialog";
import AddColumnPanel from "./components/AddColumnPanel";
import BoardFormDialog from "./components/BoardFormDialog";
import BoardAnalyticsDialog from "./components/BoardAnalyticsDialog";
import TeamTasksLoadError from "./components/TeamTasksLoadError";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import { useRealtimeTeamBoard } from "../../hooks/useRealtimeTeamBoard";
import { openTeamTasksPopupWindow } from "../../utils/openTeamTasksPopupWindow";
import { applyCardMove } from "./utils/teamBoardDnD";
import {
  canManageTeamBoard,
  canManageBoardMembers,
  canCreateTeamTask,
} from "./utils/teamBoardPermissions";
import {
  applyBoardCardFilters,
  collectBoardAssigneeOptions,
  collectBoardClientOptions,
  hasActiveBoardFilters,
  DUE_FILTERS,
  URGENCY_FILTERS,
  SORT_MODES,
} from "./utils/teamBoardCardFilters";
import { teamBoardKey } from "../../hooks/useTeamBoards";
import { TeamTasksFeedbackProvider } from "./components/TeamTasksFeedbackProvider";
import { useTeamBoardAction } from "./hooks/useTeamBoardAction";

const BoardPageInner = ({
  boardId: boardIdProp,
  spaceId,
  leadId: leadIdProp,
  compact = false,
  embedded = false,
  standalone = false,
  onBack,
  backPath = "/equipe/tarefas",
}) => {
  const { t } = useTranslation("teamTasks");
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { run, showError } = useTeamBoardAction();
  const { userData } = useAuthContext();
  const currentUserId = userData?.id;
  const { data: permissionsData } = useEffectivePermissions();
  const scopes = scopeSetFromPermissions(permissionsData, userData);

  const { data: usersData } = useUsers();
  const users = usersData?.users || usersData || [];

  const boardByIdQuery = useTeamBoard(boardIdProp, {
    enabled: Boolean(boardIdProp) && !spaceId && !leadIdProp,
  });
  const boardBySpaceQuery = useTeamBoardForSpace(spaceId, {
    enabled: Boolean(spaceId) && !boardIdProp && !leadIdProp,
  });
  const boardByLeadQuery = useTeamBoardForLead(leadIdProp, {
    enabled: Boolean(leadIdProp) && !boardIdProp && !spaceId,
  });

  const board = boardIdProp
    ? boardByIdQuery.data
    : spaceId
      ? boardBySpaceQuery.data
      : boardByLeadQuery.data;
  const isLoading = boardIdProp
    ? boardByIdQuery.isLoading
    : spaceId
      ? boardBySpaceQuery.isLoading
      : boardByLeadQuery.isLoading;
  const isError = boardIdProp
    ? boardByIdQuery.isError
    : spaceId
      ? boardBySpaceQuery.isError
      : boardByLeadQuery.isError;
  const loadError = boardIdProp
    ? boardByIdQuery.error
    : spaceId
      ? boardBySpaceQuery.error
      : boardByLeadQuery.error;
  const refetch = boardIdProp
    ? boardByIdQuery.refetch
    : spaceId
      ? boardBySpaceQuery.refetch
      : boardByLeadQuery.refetch;
  const isFetching = boardIdProp
    ? boardByIdQuery.isFetching
    : spaceId
      ? boardBySpaceQuery.isFetching
      : boardByLeadQuery.isFetching;

  const boardId = board?.id;

  const canCreate = useMemo(
    () => canCreateTeamTask(scopes, board),
    [scopes, board]
  );
  const canManage = useMemo(
    () => canManageTeamBoard(scopes, board),
    [scopes, board]
  );
  const canManageMembers = useMemo(
    () => canManageBoardMembers(scopes, board),
    [scopes, board]
  );
  const boardMembers = board?.members || [];

  const boardCacheKeys = useMemo(() => {
    const keys = [];
    if (boardIdProp) keys.push(teamBoardKey(boardIdProp));
    if (spaceId) keys.push(["teamBoardForSpace", spaceId]);
    if (leadIdProp) keys.push(["teamBoardForLead", leadIdProp]);
    return keys;
  }, [boardIdProp, spaceId, leadIdProp]);

  const mutations = useTeamBoardMutations(boardId, {
    extraCacheKeys: boardCacheKeys,
    contextLeadId: leadIdProp || null,
  });
  const skipRealtimeRef = useRef(false);

  const [selectedCard, setSelectedCard] = useState(null);
  const [cardModalOpen, setCardModalOpen] = useState(false);
  const [editBoardOpen, setEditBoardOpen] = useState(false);
  const [deleteBoardOpen, setDeleteBoardOpen] = useState(false);
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [columnToDelete, setColumnToDelete] = useState(null);
  const [deletingBoard, setDeletingBoard] = useState(false);
  const [deletingColumn, setDeletingColumn] = useState(false);
  const [organizingColumns, setOrganizingColumns] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);

  useRealtimeTeamBoard({
    boardId,
    onUpdate: () => {
      if (skipRealtimeRef.current) return;
      refetch();
    },
  });

  const userMap = useMemo(() => {
    const m = new Map();
    (users || []).forEach((u) => m.set(Number(u.id), u));
    return m;
  }, [users]);

  const enrichedColumns = useMemo(() => {
    const raw = board?.columns || [];
    return raw.map((col) => ({
      ...col,
      cards: (col.cards || []).map((card) => ({
        ...card,
        assigneeUsers: (Array.isArray(card.assigneeUsers) && card.assigneeUsers.length
          ? card.assigneeUsers
          : (card.assignees || []).map((id) => ({ id }))
        ).map((entry) => {
          const id = Number(entry?.id ?? entry);
          const user = userMap.get(id);
          return {
            id,
            nome: entry?.nome || user?.nome || user?.name || `Usuário #${id}`,
            imagem: user?.imagem || user?.avatarUrl || entry?.avatarUrl || null,
            deleted: Boolean(entry?.deleted),
          };
        }),
      })),
    }));
  }, [board?.columns, userMap]);

  const boardFilters = useMemo(() => {
    const assigneeRaw = searchParams.get("assigneeId");
    const assigneeId =
      assigneeRaw != null && assigneeRaw !== "" && Number.isFinite(Number(assigneeRaw))
        ? Number(assigneeRaw)
        : null;
    const dueRaw = searchParams.get("due");
    const due = Object.values(DUE_FILTERS).includes(dueRaw) ? dueRaw : null;
    const urgencyRaw = searchParams.get("urgency");
    const urgency = Object.values(URGENCY_FILTERS).includes(urgencyRaw) ? urgencyRaw : null;
    const clientRaw = searchParams.get("clientId");
    const clientId =
      clientRaw != null && clientRaw !== "" && Number.isFinite(Number(clientRaw))
        ? Number(clientRaw)
        : null;
    const sortRaw = searchParams.get("sort");
    const sort = sortRaw === SORT_MODES.DUE ? SORT_MODES.DUE : SORT_MODES.BOARD;
    return { assigneeId, due, urgency, clientId, sort };
  }, [searchParams]);

  const filtersActive = hasActiveBoardFilters(boardFilters);

  const columns = useMemo(
    () => applyBoardCardFilters(enrichedColumns, boardFilters),
    [enrichedColumns, boardFilters]
  );

  const assigneeFilterOptions = useMemo(
    () => collectBoardAssigneeOptions(enrichedColumns, boardMembers, users),
    [enrichedColumns, boardMembers, users]
  );
  const clientFilterOptions = useMemo(
    () => collectBoardClientOptions(enrichedColumns, board),
    [enrichedColumns, board]
  );

  const setBoardFilters = useCallback(
    (nextFilters) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (nextFilters.assigneeId != null && nextFilters.assigneeId !== "") {
          next.set("assigneeId", String(nextFilters.assigneeId));
        } else {
          next.delete("assigneeId");
        }
        if (nextFilters.due) next.set("due", String(nextFilters.due));
        else next.delete("due");
        if (nextFilters.urgency) next.set("urgency", String(nextFilters.urgency));
        else next.delete("urgency");
        if (nextFilters.clientId != null && nextFilters.clientId !== "") {
          next.set("clientId", String(nextFilters.clientId));
        } else {
          next.delete("clientId");
        }
        if (nextFilters.sort === SORT_MODES.DUE) next.set("sort", SORT_MODES.DUE);
        else next.delete("sort");
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (searchParams.get("organize") !== "1") return;
    setOrganizingColumns(true);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("organize");
      return next;
    }, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const cardParam = searchParams.get("card");
    const cardId = cardParam != null && Number.isFinite(Number(cardParam)) ? Number(cardParam) : null;
    if (!cardId || !enrichedColumns.length || cardModalOpen) return;

    for (const col of enrichedColumns) {
      const found = (col.cards || []).find((c) => Number(c.id) === cardId);
      if (found) {
        setSelectedCard({ ...found, columnId: col.id });
        setCardModalOpen(true);
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete("card");
          return next;
        }, { replace: true });
        break;
      }
    }
  }, [enrichedColumns, searchParams, setSearchParams, cardModalOpen]);

  const boardStats = useMemo(() => {
    let openCount = 0;
    let doneCount = 0;
    let totalCount = 0;
    for (const col of enrichedColumns) {
      const count = col.cards?.length || 0;
      totalCount += count;
      if (col.isDone) doneCount += count;
      else openCount += count;
    }
    return { openCount, doneCount, totalCount };
  }, [enrichedColumns]);

  const filteredStats = useMemo(() => {
    let openCount = 0;
    let doneCount = 0;
    let totalCount = 0;
    for (const col of columns) {
      const count = col.cards?.length || 0;
      totalCount += count;
      if (col.isDone) doneCount += count;
      else openCount += count;
    }
    return { openCount, doneCount, totalCount };
  }, [columns]);

  const isEmbedded = embedded || (compact && Boolean(spaceId) && !standalone);

  const embeddedCanvasSx = useMemo(
    () =>
      isEmbedded
        ? {
            bgcolor:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.background.default, 0.55)
                : "#e9edef",
            backgroundImage:
              theme.palette.mode === "dark"
                ? "none"
                : `radial-gradient(${alpha(theme.palette.primary.main, 0.035)} 1px, transparent 1px)`,
            backgroundSize: "20px 20px",
          }
        : {},
    [isEmbedded, theme]
  );

  const onDragEnd = useCallback(
    (result) => {
      const { destination, source, draggableId, type } = result;
      if (!destination || !boardId || !board) return;
      if (source.droppableId === destination.droppableId && source.index === destination.index) {
        return;
      }

      if (type === "COLUMN") {
        if (!organizingColumns || !canManage) return;
        const nextColumns = Array.from(enrichedColumns);
        const [removed] = nextColumns.splice(source.index, 1);
        nextColumns.splice(destination.index, 0, removed);
        const columnIds = nextColumns.map((c) => Number(c.id));
        const optimisticBoard = {
          ...board,
          columns: nextColumns.map((col, index) => ({ ...col, position: index })),
        };
        mutations.setBoardCache(optimisticBoard);
        skipRealtimeRef.current = true;
        mutations.reorderColumns.mutate(columnIds, {
          onSuccess: (data) => {
            mutations.setBoardCache(data);
          },
          onError: (err) => {
            refetch();
            showError(err, "reorderColumns");
          },
          onSettled: () => {
            window.setTimeout(() => {
              skipRealtimeRef.current = false;
            }, 800);
          },
        });
        return;
      }

      if (organizingColumns || filtersActive) return;

      const cardId = Number(draggableId);
      const destColumnId = Number(destination.droppableId);
      const sourceColumnId = Number(source.droppableId);
      const position = destination.index;

      const optimisticBoard = applyCardMove(board, {
        cardId,
        sourceColumnId,
        destColumnId,
        destIndex: position,
      });
      mutations.setBoardCache(optimisticBoard);

      skipRealtimeRef.current = true;
      mutations.moveCard.mutate(
        { cardId, columnId: destColumnId, position },
        {
          onSuccess: (data) => {
            mutations.setBoardCache(data);
          },
          onError: (err) => {
            refetch();
            showError(err, "moveCard");
          },
          onSettled: () => {
            window.setTimeout(() => {
              skipRealtimeRef.current = false;
            }, 800);
          },
        }
      );
    },
    [
      board,
      boardId,
      canManage,
      enrichedColumns,
      filtersActive,
      mutations,
      organizingColumns,
      refetch,
      showError,
    ]
  );

  const handleAddCard = async (columnId, title) => {
    const result = await run(
      () =>
        mutations.createCard.mutateAsync({
          columnId,
          title,
          ...(leadIdProp
            ? { leadId: leadIdProp, contextLeadId: leadIdProp }
            : {}),
        }),
      { context: "createCard", successKey: "successCardCreated" }
    );
    return result !== null;
  };

  const handleAddColumn = async (name) => {
    const result = await run(
      () => mutations.createColumn.mutateAsync({ name }),
      { context: "createColumn", successKey: "successColumnCreated" }
    );
    return result !== null;
  };

  const handleUpdateColumn = async (columnId, payload) => {
    const result = await run(
      () => mutations.updateColumn.mutateAsync({ columnId, ...payload }),
      { context: "updateColumn" }
    );
    return result !== null;
  };

  const handleDeleteColumn = async () => {
    if (!columnToDelete) return;
    setDeletingColumn(true);
    try {
      const result = await run(
        () => mutations.deleteColumn.mutateAsync(columnToDelete.id),
        { context: "deleteColumn", successKey: "successColumnDeleted" }
      );
      if (result !== null) setColumnToDelete(null);
    } finally {
      setDeletingColumn(false);
    }
  };

  const handleEditBoard = async (payload) => {
    const result = await run(
      () => mutations.updateBoard.mutateAsync(payload),
      { context: "updateBoard", successKey: "successBoardSaved" }
    );
    if (result !== null) {
      setEditBoardOpen(false);
      return true;
    }
    return false;
  };

  const handleDeleteBoard = async () => {
    setDeletingBoard(true);
    try {
      const result = await run(
        () => mutations.deleteBoard.mutateAsync(),
        { context: "deleteBoard", successKey: "successBoardDeleted" }
      );
      if (result === null) return;
      setDeleteBoardOpen(false);
      if (standalone && backPath) navigate(backPath);
      else if (standalone) window.close();
      else navigate(backPath);
    } finally {
      setDeletingBoard(false);
    }
  };

  const handleOpenPopup = () => {
    const resolvedBoardId = board?.id || boardIdProp;
    openTeamTasksPopupWindow(
      resolvedBoardId
        ? { boardId: resolvedBoardId }
        : { leadId: leadIdProp, spaceId }
    );
  };

  const handleBack = () => {
    if (onBack) onBack();
    else navigate(backPath);
  };

  const boardCanvasBg = isEmbedded
    ? "transparent"
    : theme.palette.mode === "dark"
      ? `linear-gradient(165deg, ${alpha(theme.palette.primary.dark, 0.32)} 0%, ${theme.palette.background.default} 45%)`
      : `linear-gradient(165deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${theme.palette.background.default} 50%)`;

  const boardCanvasPattern =
    isEmbedded || theme.palette.mode === "dark"
      ? {}
      : {
          backgroundImage: `radial-gradient(${alpha(theme.palette.primary.main, 0.045)} 1px, transparent 1px)`,
          backgroundSize: "22px 22px",
        };

  const pageHeight = compact || isEmbedded ? "100%" : standalone ? "100vh" : "calc(100vh - 64px)";

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !board) {
    return (
      <Box sx={{ p: 3, maxWidth: 900, mx: "auto" }}>
        <TeamTasksLoadError
          error={loadError}
          onRetry={() => refetch()}
          isRetrying={isFetching}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: pageHeight,
        minHeight: 0,
        bgcolor: boardCanvasBg,
        backgroundAttachment: isEmbedded ? undefined : "fixed",
        ...boardCanvasPattern,
        ...embeddedCanvasSx,
      }}
    >
      {isEmbedded ? (
        <ChannelBoardToolbar
          stats={filtersActive ? filteredStats : boardStats}
          onOpenPopup={handleOpenPopup}
          onOpenAnalytics={() => setAnalyticsOpen(true)}
        />
      ) : null}

      {!isEmbedded ? (
        <BoardHeader
          board={{ ...board, columns: enrichedColumns }}
          members={boardMembers}
          onMembersClick={() => setMembersDialogOpen(true)}
          compact={compact}
          standalone={standalone}
          canManage={canManage}
          organizingColumns={organizingColumns}
          onToggleOrganizeColumns={() => setOrganizingColumns((v) => !v)}
          onBack={handleBack}
          onEdit={() => setEditBoardOpen(true)}
          onDelete={() => setDeleteBoardOpen(true)}
          onOpenAnalytics={() => setAnalyticsOpen(true)}
          onOpenPopup={standalone ? undefined : handleOpenPopup}
          flushBottom
        />
      ) : null}

      <BoardFiltersBar
        filters={boardFilters}
        onChange={setBoardFilters}
        assigneeOptions={assigneeFilterOptions}
        clientOptions={clientFilterOptions}
        compact={compact}
        embedded={isEmbedded}
      />

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="board-columns" direction="horizontal" type="COLUMN">
          {(columnsProvided) => (
            <Box
              ref={columnsProvided.innerRef}
              {...columnsProvided.droppableProps}
              sx={{
                display: "flex",
                gap: isEmbedded ? 1.25 : 1.5,
                overflowX: "auto",
                overflowY: "hidden",
                flex: 1,
                px: isEmbedded ? { xs: 1, sm: 1.5 } : compact ? 1.25 : 2,
                py: isEmbedded ? { xs: 1.25, sm: 1.5 } : 1.5,
                minHeight: 0,
                alignItems: "stretch",
                scrollbarWidth: "thin",
                outline: organizingColumns
                  ? `2px dashed ${alpha(theme.palette.primary.main, 0.35)}`
                  : "none",
                outlineOffset: -4,
                borderRadius: organizingColumns ? 2 : 0,
                "&::-webkit-scrollbar": { height: 6 },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: alpha(theme.palette.text.primary, 0.12),
                  borderRadius: 999,
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  bgcolor: alpha(theme.palette.text.primary, 0.22),
                },
              }}
            >
              {columns.map((col, colIndex) => (
                <Draggable
                  key={col.id}
                  draggableId={`column-${col.id}`}
                  index={colIndex}
                  isDragDisabled={!organizingColumns || !canManage}
                >
                  {(colProvided, colSnapshot) => (
                    <Box
                      ref={colProvided.innerRef}
                      {...colProvided.draggableProps}
                      sx={{
                        height: "100%",
                        opacity: colSnapshot.isDragging ? 0.92 : 1,
                      }}
                    >
                      <BoardColumn
                        column={col}
                        embedded={isEmbedded}
                        organizingColumns={organizingColumns}
                        cardsDragDisabled={filtersActive}
                        columnDragHandleProps={
                          organizingColumns ? colProvided.dragHandleProps : null
                        }
                        onAddCard={handleAddCard}
                        onOpenCard={(card) => {
                          setSelectedCard(card);
                          setCardModalOpen(true);
                        }}
                        onUpdateColumn={handleUpdateColumn}
                        onDeleteColumn={setColumnToDelete}
                        canManage={canManage}
                        canCreate={canCreate}
                      />
                    </Box>
                  )}
                </Draggable>
              ))}
              {columnsProvided.placeholder}
              {canManage && !organizingColumns && (
                <AddColumnPanel
                  embedded={isEmbedded}
                  onAdd={handleAddColumn}
                />
              )}
            </Box>
          )}
        </Droppable>
      </DragDropContext>

      <TaskCardModalContainer
        open={cardModalOpen}
        card={selectedCard}
        board={board ? { ...board, columns: enrichedColumns } : null}
        onClose={() => {
          setCardModalOpen(false);
          setSelectedCard(null);
        }}
        extraCacheKeys={boardCacheKeys}
        contextLeadId={leadIdProp || null}
      />

      <BoardAnalyticsDialog
        open={analyticsOpen}
        onClose={() => setAnalyticsOpen(false)}
        boardId={boardId}
        boardName={board?.name}
        assigneeOptions={assigneeFilterOptions}
        clientOptions={clientFilterOptions}
        columns={enrichedColumns}
      />

      <BoardFormDialog
        open={editBoardOpen}
        onClose={() => setEditBoardOpen(false)}
        mode="edit"
        initialValues={{ name: board.name, description: board.description }}
        onSubmit={handleEditBoard}
        isLoading={mutations.updateBoard.isLoading}
      />

      <BoardMembersDialog
        open={membersDialogOpen}
        onClose={() => setMembersDialogOpen(false)}
        board={board}
        members={boardMembers}
        currentUserId={currentUserId}
        currentUserRole={board?.currentUserRole || null}
        canManageMembers={canManageMembers}
        isBusy={
          mutations.addMember.isLoading ||
          mutations.removeMember.isLoading ||
          mutations.updateMemberRole.isLoading
        }
        onAddMember={(payload) =>
          run(() => mutations.addMember.mutateAsync(payload), {
            context: "addMember",
            successKey: "successMemberAdded",
          })
        }
        onUpdateMemberRole={(memberId, role) =>
          run(() => mutations.updateMemberRole.mutateAsync({ memberId, role }), {
            context: "updateMemberRole",
            successKey: "successMemberRoleUpdated",
          })
        }
        onRemoveMember={(memberId) =>
          run(() => mutations.removeMember.mutateAsync(memberId), {
            context: "removeMember",
            successKey: "successMemberRemoved",
          })
        }
      />

      <ConfirmDialog
        open={deleteBoardOpen}
        title={t("deleteBoardTitle")}
        content={t("deleteBoardBody")}
        confirmText={t("deleteBoardConfirm")}
        cancelText={t("cancel")}
        onClose={() => !deletingBoard && setDeleteBoardOpen(false)}
        onConfirm={handleDeleteBoard}
        loading={deletingBoard}
      />

      <ConfirmDialog
        open={Boolean(columnToDelete)}
        title={t("deleteColumnTitle")}
        content={
          (columnToDelete?.cards?.length || 0) > 0
            ? t("deleteColumnBodyWithCards", { count: columnToDelete.cards.length })
            : t("deleteColumnBody")
        }
        confirmText={t("deleteColumnConfirm")}
        cancelText={t("cancel")}
        onClose={() => !deletingColumn && setColumnToDelete(null)}
        onConfirm={handleDeleteColumn}
        loading={deletingColumn}
      />
    </Box>
  );
};

const BoardPage = (props) => (
  <TeamTasksFeedbackProvider>
    <BoardPageInner {...props} />
  </TeamTasksFeedbackProvider>
);

export default BoardPage;
